import {
  TLBoundsCorner,
  TLSnapLine,
  TLBoundsEdge,
  Utils,
  TLBoundsWithCenter
} from '@tldraw/core'
import {Vec} from '@tldraw/vec'
import {SessionType, TldrawCommand, TldrawPatch, TDShape, SLOW_SPEED, SNAP_DISTANCE, WhiteboardStore, TLDR} from '@modules/whiteboard'
import {BaseSession} from '@modules/whiteboard/sessions/BaseSession'
import {action, computed, observable, toJS} from 'mobx'
//import {BaseSession} from '@modules/whiteboard'

type SnapInfo =
  | {
  state: 'empty'
}
  | {
  state: 'ready'
  bounds: TLBoundsWithCenter[]
}

export class TransformSingleSession extends BaseSession {
  type = SessionType.TransformSingle

  @observable scaleX = 1
  @observable scaleY = 1

  @observable initialShape: TDShape

  @computed get initialShapeBounds() {
    return this.app.shapeUtil.getBounds(this.initialShape)
  }

  @computed get initialCommonBounds() { return this.app.shapeUtil.getRotatedBounds(this.initialShape) }

  @observable snapInfo: SnapInfo = {state: 'empty'}
  @observable prevPoint = [0, 0]
  @observable speed = 1

  constructor(app: WhiteboardStore, public shapeId: string, public transformType: TLBoundsEdge | TLBoundsCorner, public isCreate = false) {
    super(app)

    this.initialShape = _.cloneDeep(app.getShape(this.shapeId))
    app.ui.rotationInfo.selectedIds = [shapeId]
  }

  @action start = (): TldrawPatch | undefined => {
    this.status = 'transforming'

    this.snapInfo = {
      state: 'ready',
      bounds: this.app.shapes
        .filter((shape) => shape.id !== this.shapeId)
        .map((shape) => Utils.getBoundsWithCenter(this.app.shapeUtil.getRotatedBounds(shape)))
    }

    return void null
  }

  @action update = (): TldrawPatch | undefined => {
    const {
        transformType, initialShape,
        initialShape: {isLocked}, initialShapeBounds,
        app, app: {
          getShape, grid,
          settings: {ui: {isSnapping, viewport}}, appState,
          myPageState: {camera},
          currentPoint,
          previousPoint,
          originPoint,
          shiftKey,
          altKey,
          metaKey
        }
      } = this,
      shape = getShape(initialShape.id),
      utils = app.shapeUtilFor(shape)

    if (isLocked) return void null

    const delta = altKey
                  ? Vec.mul(Vec.sub(currentPoint, originPoint), 2)
                  : Vec.sub(currentPoint, originPoint)

    let newBounds = Utils.getTransformedBoundingBox(
      initialShapeBounds,
      transformType,
      delta,
      shape.rotation,
      shiftKey || shape.isAspectRatioLocked || utils.isAspectRatioLocked
    )

    if (altKey) {
      newBounds = {
        ...newBounds,
        ...Utils.centerBounds(newBounds, Utils.getBoundsCenter(initialShapeBounds))
      }
    }

    grid && _.assign(newBounds, Utils.snapBoundsToGrid(newBounds, grid))

    // Should we snap?

    const speed = Vec.dist(currentPoint, previousPoint),
      speedChange = speed - this.speed

    this.speed += speedChange * (speedChange > 1 ? 0.5 : 0.15)

    let snapLines: TLSnapLine[] = []

    if (
      ((isSnapping && !metaKey) || (!isSnapping && metaKey)) &&
      !initialShape.rotation && // not now anyway
      this.speed * camera.zoom < SLOW_SPEED &&
      this.snapInfo.state === 'ready'
    ) {
      const snapResult = Utils.getSnapPoints(
        Utils.getBoundsWithCenter(newBounds),
        this.snapInfo.bounds.filter(
          (bounds) => Utils.boundsContain(viewport, bounds) || Utils.boundsCollide(viewport, bounds)
        ),
        SNAP_DISTANCE / camera.zoom
      )

      if (snapResult) {
        snapLines = snapResult.snapLines

        newBounds = Utils.getTransformedBoundingBox(
          initialShapeBounds,
          transformType,
          Vec.sub(delta, snapResult.offset),
          shape.rotation,
          shiftKey || shape.isAspectRatioLocked || utils.isAspectRatioLocked
        )
      }
    }

    const afterShape = utils.transformSingle(shape, newBounds, {
      initialShape,
      type: this.transformType,
      scaleX: newBounds.scaleX,
      scaleY: newBounds.scaleY,
      transformOrigin: [0.5, 0.5]
    })

    //console.log(toJS({shape, afterShape}, {recurseEverything: true}))

    if (grid && afterShape && afterShape.point) {
      afterShape.point = Vec.snap(afterShape.point, grid)
    }

    if (afterShape) {
      _.assign(shape, afterShape)
    }

    appState.snapLines.replace(snapLines)
  }

  @action cancel = (): TldrawPatch | undefined => {
    const {
      shapeId, initialShape,
      app: {appState, shapes, myPageState}
    } = this

    if (this.isCreate) {
      delete shapes[shapeId]
    } else {
      shapes[shapeId] = initialShape
    }

    appState.snapLines.replace([])
    myPageState.selectedIds = this.isCreate ? [] : [shapeId]

    return undefined
  }

  @action complete = (): TldrawPatch | TldrawCommand | undefined => {
    const {
        shapeId, initialShape,
        app: {getShape, appState, selectedPageInfo: {state}, shapeUtil: {onSessionComplete}}
      } = this,
      shape = getShape(initialShape.id)

    shape.isGenerated = false

    if (initialShape.isLocked) return

    _.merge(shape, onSessionComplete(shape))
    appState.snapLines.clear()
    _.merge(state, {
      selectedIds: [shapeId],
      editingId: undefined,
      hoveredId: undefined
    })
  }
}



// WEBPACK FOOTER //
// ./src/modules/whiteboard/sessions/TransformSingleSession/TransformSingleSession.ts